// src/pages/calendar/Calendar.js
import React, { useMemo, useState, useEffect, useCallback, memo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getMeetingsByMonth, clearCalendarMeetings } from "../../features/meeting/meetingSlice";
import CalendarSearch from "./calendarsearch";
import { getDetailPath } from "../../utils/meetingUtils";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const WEEKDAYS_SHORT_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

/* ------------ Optimized Utility Functions ------------ */
const toDate = (v) => v?.toDate ? v.toDate() : new Date(v);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const pad = (n) => String(n).padStart(2, "0");
const keyOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Cache for expensive calculations
const dateCache = new Map();

const startOfWeek = (d) => {
  const key = `start-${d.getTime()}`;
  if (dateCache.has(key)) return dateCache.get(key);

  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);

  dateCache.set(key, x);
  return x;
};

const endOfWeek = (d) => {
  const key = `end-${d.getTime()}`;
  if (dateCache.has(key)) return dateCache.get(key);

  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);

  dateCache.set(key, e);
  return e;
};

const monthGrid = (year, month) => {
  const key = `grid-${year}-${month}`;
  if (dateCache.has(key)) return dateCache.get(key);

  const first = new Date(year, month, 1);
  const gridStart = startOfWeek(first);
  const cells = [];

  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

  dateCache.set(key, rows);
  return rows;
};

const weekRow = (containingDate) => {
  const s = startOfWeek(containingDate);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(s);
    d.setDate(s.getDate() + i);
    return d;
  });
};

const isDateInCurrentMonth = (date, currentMonth, currentYear) =>
  date.getMonth() === currentMonth && date.getFullYear() === currentYear;

/* --------------- Memoized Event Item Component --------------- */
const EventItem = memo(({ event, mode, isCurrentMonth, onNavigate }) => {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    const path = getDetailPath(event);
    if (path) onNavigate(path);
  }, [event, onNavigate]);

  const [isHovered, setIsHovered] = useState(false);

  const itemStyle = useMemo(() => ({
    backgroundColor: isHovered ? 'rgba(148,163,184,0.15)' : 'transparent',
    color: 'inherit',
    fontSize: mode === "week" ? '12px' : '12px',
    lineHeight: '1.2',
    minHeight: mode === "week" ? '22px' : '20px',
    padding: '2px 4px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    opacity: !isCurrentMonth ? 0.7 : 1
  }), [mode, isCurrentMonth, isHovered]);

  const dotColor = event.isActivity ? '#7c3aed' : '#2563eb';

  return (
    <div
      className="event-item border-0 position-relative"
      style={itemStyle}
      title={`${event.time} • ${event.title}${event.attendees > 0 ? ` (${event.attendees} kişi)` : ''}`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="event-line">
        <span className="event-dot" style={{ backgroundColor: dotColor }} />
        <span className="event-time">{event.time}</span>
        <span className="event-title">{event.title}</span>
      </div>
    </div>
  );
});

EventItem.displayName = 'EventItem';

/* --------------- Memoized Calendar Cell Component --------------- */
const CalendarCell = memo(({
  date,
  isToday,
  isSelected,
  isCurrentMonth,
  mode,
  dayEvents,
  onDateClick,
  onShowMore,
  onNavigate
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleDateClick = useCallback(() => {
    onDateClick(date);
  }, [date, onDateClick]);

  const handleShowMore = useCallback((e) => {
    onShowMore(date, dayEvents, e);
  }, [date, dayEvents, onShowMore]);

  const cellStyle = useMemo(() => ({
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    position: 'relative'
  }), []);

  const maxEvents = mode === "week" ? 8 : 4;
  const visibleEvents = dayEvents.slice(0, maxEvents);
  const hasMoreEvents = dayEvents.length > maxEvents;

  return (
    <div
      className={`col calendar-cell ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''} ${!isCurrentMonth ? 'is-out' : ''}`}
      style={cellStyle}
      onClick={handleDateClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-1 h-100 d-flex flex-column calendar-cell-inner">
        {/* Date Number */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <span className={`calendar-date ${isSelected ? 'is-selected' : ''} ${isToday ? 'is-today' : ''} ${!isCurrentMonth ? 'is-out' : ''}`}>
            {date.getDate()}
          </span>
        </div>

        {/* Events Container */}
        <div className="flex-grow-1 d-flex flex-column gap-1" style={{ maxHeight: "calc(100% - 32px)", overflowY: 'auto' }}>
          {visibleEvents.map((event, idx) => (
            <EventItem
              key={`${event.id}-${idx}`}
              event={event}
              mode={mode}
              isCurrentMonth={isCurrentMonth}
              onNavigate={onNavigate}
            />
          ))}

          {hasMoreEvents && (
            <div
              className="more-events-indicator text-center rounded-1"
              style={{
                fontSize: '10px',
                marginTop: '2px',
                padding: '2px 4px',
                backgroundColor: 'rgba(0,0,0,0.1)',
                color: !isCurrentMonth ? '#9aa0a6' : '#5f6368',
                fontWeight: '500',
                cursor: 'pointer'
              }}
              onClick={handleShowMore}
            >
              +{dayEvents.length - maxEvents} toplantı daha
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

CalendarCell.displayName = 'CalendarCell';

/* --------------- Enhanced Calendar Component --------------- */
export default function Calendar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { calendarMeetings = [], loadingStates, errors } = useSelector(s => s.meeting) || {};
  const isLoading = loadingStates?.getMeetingsByMonth;
  const error = errors?.getMeetingsByMonth;

  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [mode, setMode] = useState("month");
  const [cursor, setCursor] = useState(new Date(monthStart));
  const [selected, setSelected] = useState(today);
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalEvents, setModalEvents] = useState([]);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const viewRef = useRef(null);
  const searchRef = useRef(null);

  const currentYear = cursor.getFullYear();
  useEffect(() => {
    dispatch(clearCalendarMeetings());
    for (let m = 0; m < 12; m += 1) {
      dispatch(getMeetingsByMonth({ year: currentYear, monthIndex: m }));
    }
  }, [dispatch, currentYear]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (viewRef.current && !viewRef.current.contains(e.target)) {
        setIsViewOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Memoized navigation handlers
  const goPrev = useCallback(() => {
    setCursor(prev => {
      if (mode === "day") {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() - 1);
        return newDate;
      } else if (mode === "week") {
        const s = startOfWeek(prev);
        const newDate = new Date(s);
        newDate.setDate(s.getDate() - 7);
        return newDate;
      } else {
        const y = prev.getFullYear(), m = prev.getMonth();
        return new Date(y, m - 1, 1);
      }
    });
  }, [mode]);

  const goNext = useCallback(() => {
    setCursor(prev => {
      if (mode === "day") {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() + 1);
        return newDate;
      } else if (mode === "week") {
        const s = startOfWeek(prev);
        const newDate = new Date(s);
        newDate.setDate(s.getDate() + 7);
        return newDate;
      } else {
        const y = prev.getFullYear(), m = prev.getMonth();
        return new Date(y, m + 1, 1);
      }
    });
  }, [mode]);

  const goToToday = useCallback(() => {
    setSelected(today);
    if (mode === "day") {
      setCursor(new Date(today));
    } else if (mode === "week") {
      setCursor(startOfWeek(today));
    } else {
      setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    }
  }, [mode, today]);

  const handleDateClick = useCallback((date) => {
    setSelected(date);
    if (mode === "month") {
      if (!isDateInCurrentMonth(date, cursor.getMonth(), cursor.getFullYear())) {
        setCursor(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    } else if (mode === "week") {
      setMode("day");
      setCursor(new Date(date));
    }
  }, [mode, cursor]);

  const handleShowMore = useCallback((date, events, e) => {
    e.stopPropagation();
    setModalDate(date);
    setModalEvents(events);
    setShowMoreModal(true);
  }, []);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);
  const openDetail = useCallback((event) => {
    const path = getDetailPath(event);
    if (path) navigate(path);
  }, [navigate]);

  // Calculate visible range (optimized with useMemo)
  const { visibleStart, visibleEnd, displayTitle } = useMemo(() => {
    if (mode === "day") {
      const dayStart = new Date(cursor);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(cursor);
      dayEnd.setHours(23, 59, 59, 999);
      return {
        visibleStart: dayStart,
        visibleEnd: dayEnd,
        displayTitle: cursor.toLocaleDateString("tr-TR", {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      };
    }
    if (mode === "week") {
      const start = startOfWeek(cursor);
      const end = endOfWeek(cursor);
      return {
        visibleStart: start,
        visibleEnd: end,
        displayTitle: `${start.toLocaleDateString("tr-TR", { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString("tr-TR", { day: 'numeric', month: 'short', year: 'numeric' })}`
      };
    }
    const rows = monthGrid(cursor.getFullYear(), cursor.getMonth());
    const vs = new Date(rows[0][0]); vs.setHours(0, 0, 0, 0);
    const ve = new Date(rows[5][6]); ve.setHours(23, 59, 59, 999);
    return {
      visibleStart: vs,
      visibleEnd: ve,
      displayTitle: `${MONTHS_TR[cursor.getMonth()]} ${cursor.getFullYear()}`
    };
  }, [mode, cursor]);

  // Optimized events grouping - only process visible dates
  const eventsByDay = useMemo(() => {
    if (!calendarMeetings.length) return {};

    const map = {};
    const startTime = visibleStart.getTime();
    const endTime = visibleEnd.getTime();

    for (const m of calendarMeetings) {
      if (!m?.startDate) continue;

      const d = toDate(m.startDate);
      const dateTime = d.getTime();

      // Skip dates outside visible range
      if (dateTime < startTime || dateTime > endTime) continue;

      const k = keyOf(d);
      const event = {
        id: m.meetingId,
        title: m.title || "(Başlıksız)",
        time: d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
        isActivity: !!m.isactivity,
        priority: m.priority || 'normal',
        attendees: m.attendees?.length || 0,
        duration: m.duration || 60,
      };

      if (!map[k]) map[k] = [];
      map[k].push(event);
    }

    // Sort events by time
    Object.values(map).forEach(list =>
      list.sort((a, b) => a.time.localeCompare(b.time, "tr"))
    );

    return map;
  }, [calendarMeetings, visibleStart, visibleEnd]);

  // Grid data (memoized)
  const rows = useMemo(() => {
    if (mode === "day") {
      return [[cursor]];
    }
    return mode === "week"
      ? [weekRow(cursor)]
      : monthGrid(cursor.getFullYear(), cursor.getMonth());
  }, [mode, cursor]);

  // Loading state
  if (isLoading && !calendarMeetings.length) {
    return (
      <div className="container-fluid p-4">
        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <p className="text-muted">Takvim yükleniyor...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Hata!</h4>
          <p>Takvim verileri yüklenirken bir hata oluştu.</p>
          <button className="btn btn-outline-danger" onClick={() => {
            dispatch(clearCalendarMeetings());
            for (let m = 0; m < 12; m += 1) {
              dispatch(getMeetingsByMonth({ year: currentYear, monthIndex: m }));
            }
          }}>
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid p-3">
        <div className="card calendar-theme shadow-sm border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>

          {/* Modern Header */}
          <div className="card-header calendar-header border-0 p-2">
            <div className="calendar-header-inner">
              <div className="calendar-header-left">
                <button className="cal-pill" onClick={goToToday} title="Bugüne Git">
                  Bugün
                </button>
                <div className="cal-nav">
                  <button className="cal-icon" onClick={goPrev} title="Önceki">‹</button>
                  <button className="cal-icon" onClick={goNext} title="Sonraki">›</button>
                </div>
                <div className="calendar-title">{displayTitle}</div>
              </div>

              <div className="calendar-header-right">
                <button
                  className="cal-icon"
                  onClick={() => setIsSearchOpen((v) => !v)}
                  title="Ara"
                  aria-label="Ara"
                >
                  <i className="fa fa-search" />
                </button>


                <div className="cal-view" ref={viewRef}>
                  <button
                    className="cal-pill cal-pill-dark"
                    onClick={() => setIsViewOpen((v) => !v)}
                    aria-expanded={isViewOpen}
                  >
                    {mode === "month" ? "Ay" : mode === "week" ? "Hafta" : "Gün"} <span className="caret">▾</span>
                  </button>
                  {isViewOpen && (
                    <div className="cal-dropdown">
                      <button
                        className={`cal-dropdown-item ${mode === "month" ? "active" : ""}`}
                        onClick={() => {
                          setMode("month");
                          setCursor(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
                          setIsViewOpen(false);
                        }}
                      >
                        Ay
                      </button>
                      <button
                        className={`cal-dropdown-item ${mode === "week" ? "active" : ""}`}
                        onClick={() => {
                          setMode("week");
                          setCursor(startOfWeek(selected));
                          setIsViewOpen(false);
                        }}
                      >
                        Hafta
                      </button>
                      <button
                        className={`cal-dropdown-item ${mode === "day" ? "active" : ""}`}
                        onClick={() => {
                          setMode("day");
                          setCursor(new Date(selected));
                          setIsViewOpen(false);
                        }}
                      >
                        Gün
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isSearchOpen && (
              <div className="calendar-search-pop" ref={searchRef}>
                <CalendarSearch
                  meetings={calendarMeetings}
                  onSelectDay={(date) => {
                    setCursor(new Date(date));
                    setMode("day");
                    setIsSearchOpen(false);
                  }}
                />
              </div>
            )}
          </div>

          <div className="card-body p-0 calendar-body">

            {/* Weekday Headers - Hide in day view */}
            {mode !== "day" && (
              <div className="calendar-weekdays">
                <div className="row g-0">
                  {WEEKDAYS_SHORT_TR.map((dayName) => (
                    <div key={dayName} className="col text-center py-3">
                      <div className="fw-semibold text-muted">{dayName}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Calendar Grid */}
            <div className={`calendar-grid ${mode}-view`}>
              {mode === "day" ? (
                // Day View - Single detailed view
                <div className="day-view p-4">
                  {(() => {
                    const dayKey = keyOf(cursor);
                    const dayEvents = eventsByDay[dayKey] || [];
                    const isToday = sameDay(cursor, today);

                    return (
                      <div className="day-container">
                        <div className="day-header">
                          <div className="day-title">
                            <span className="day-number">{cursor.getDate()}</span>
                            <span className="day-name">
                              {cursor.toLocaleDateString("tr-TR", { weekday: 'long' })}
                            </span>
                            {isToday && <span className="day-pill">Bugün</span>}
                          </div>
                          <div className="day-count">
                            {dayEvents.length} toplantı
                          </div>
                        </div>

                        {dayEvents.length === 0 ? (
                          <div className="text-center py-5">
                            <h5 className="text-muted">Bu gün için toplantı bulunmuyor</h5>
                            <p className="text-muted small">Yeni bir toplantı ekleyebilirsiniz.</p>
                          </div>
                        ) : (
                          <div className="events-list day-cards">
                            {dayEvents.map((event, idx) => (
                              <div
                                key={`day-event-${event.id}-${idx}`}
                                className={`event-card day-card ${event.isActivity ? 'is-activity' : 'is-meeting'}`}
                                onClick={() => openDetail(event)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <div className="day-card-body">
                                  <div className="day-card-top">
                                    <span className="day-time">{event.time}</span>
                                    <span className="day-chip">{event.isActivity ? 'Etkinlik' : 'Toplantı'}</span>
                                  </div>
                                  <div className="day-title-row">
                                    <span className="day-dot" />
                                    <h5 className="day-card-title">{event.title}</h5>
                                  </div>
                                  {event.attendees > 0 && (
                                    <div className="day-meta">
                                      {event.attendees} katılımcı
                                    </div>
                                  )}
                                </div>
                                <div className="day-card-arrow">›</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                // Month/Week View - Grid layout with memoized cells
                rows.map((week, weekIndex) => (
                  <div key={`week-${weekIndex}`} className="row g-0 calendar-row">
                    {week.map((date, dayIndex) => {
                      const isToday = sameDay(date, today);
                      const isSelected = sameDay(date, selected);
                      const isCurrentMonth = isDateInCurrentMonth(date, cursor.getMonth(), cursor.getFullYear());
                      const dayKey = keyOf(date);
                      const dayEvents = eventsByDay[dayKey] || [];

                      return (
                        <CalendarCell
                          key={`cell-${dayKey}`}
                          date={date}
                          isToday={isToday}
                          isSelected={isSelected}
                          isCurrentMonth={isCurrentMonth}
                          mode={mode}
                          dayEvents={dayEvents}
                          onDateClick={handleDateClick}
                          onShowMore={handleShowMore}
                          onNavigate={handleNavigate}
                        />
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Modern Footer with Statistics */}
          <div className="card-footer bg-light border-0 p-3">
            <div className="row align-items-center">
              <div className="col-md-6">
                <small className="text-muted">
                  Toplam {calendarMeetings.length} toplantı/etkinlik
                  {mode === "month" && ` • ${MONTHS_TR[cursor.getMonth()]} ${cursor.getFullYear()}`}
                  {mode === "day" && ` • ${cursor.toLocaleDateString("tr-TR")}`}
                </small>
              </div>
              <div className="col-md-6 text-end">
                <small className="text-muted">
                  Son güncelleme: {new Date().toLocaleTimeString("tr-TR", { hour: '2-digit', minute: '2-digit' })}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for showing all events - Optimized */}
      {showMoreModal && modalDate && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={() => setShowMoreModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  {modalDate.toLocaleDateString("tr-TR", {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMoreModal(false)}
                ></button>
              </div>

              <div className="modal-body pt-0">
                <div className="mb-3">
                  <small className="text-muted">
                    {modalEvents.length} {modalEvents.length === 1 ? 'toplantı' : 'toplantı'} bulundu
                  </small>
                </div>

                <div className="events-modal-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {modalEvents.map((event, idx) => (
                    <div
                      key={`modal-event-${event.id}-${idx}`}
                      className="event-modal-item mb-3 p-3 rounded-3 border position-relative"
                      style={{
                        borderColor: event.isActivity ? '#22c55e' : '#2563eb',
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: 'white'
                      }}
                      onClick={() => {
                        openDetail(event);
                        setShowMoreModal(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = event.isActivity ? '#22c55e10' : '#2563eb10';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span
                              className="time-badge-modal rounded-2 px-3 py-1"
                              style={{
                                backgroundColor: event.isActivity ? '#22c55e' : '#2563eb',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                fontFamily: 'monospace'
                              }}
                            >
                              {event.time}
                            </span>
                            <span
                              className="badge rounded-pill"
                              style={{
                                backgroundColor: event.isActivity ? '#22c55e20' : '#2563eb20',
                                color: event.isActivity ? '#22c55e' : '#2563eb',
                                border: `1px solid ${event.isActivity ? '#22c55e' : '#2563eb'}`
                              }}
                            >
                              {event.isActivity ? 'Etkinlik' : 'Toplantı'}
                            </span>
                          </div>

                          <h6 className="mb-2 fw-bold text-dark">{event.title}</h6>

                          <div className="d-flex align-items-center gap-3 text-muted small">
                            {event.attendees > 0 && (
                              <div className="d-flex align-items-center gap-1">
                                <span>👥</span>
                                <span>{event.attendees} katılımcı</span>
                              </div>
                            )}
                            {event.duration && (
                              <div className="d-flex align-items-center gap-1">
                                <span>⏱️</span>
                                <span>{event.duration} dakika</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-end">
                          <span className="text-muted">›</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer border-0 pt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={() => setShowMoreModal(false)}
                >
                  Kapat
                </button>
                <button
                  type="button"
                  className="btn btn-primary rounded-pill px-4"
                  onClick={() => {
                    setMode("day");
                    setCursor(new Date(modalDate));
                    setShowMoreModal(false);
                  }}
                >
                  Gün Görünümüne Git
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Optimized Custom Styles */}
      <style>{`
        .calendar-theme {
          --cal-bg: #ffffff;
          --cal-surface: #ffffff;
          --cal-text: #0f172a;
          --cal-muted: #6b7280;
          --cal-line: #e2e8f0;
          --cal-hover: #f1f5f9;
          --cal-accent: #2563eb;
          --cal-event-blue: #2563eb;
          --cal-event-purple: #7c3aed;
          --cal-header-bg: linear-gradient(90deg, #1e3a8a 0%, #1d4ed8 60%, #2563eb 100%);
          --cal-header-text: #f8fafc;
          --cal-header-muted: #c7d2fe;
          --cal-header-border: #1e40af;
        }

        .calendar-theme {
          background: var(--cal-bg);
        }

        .calendar-theme .calendar-header {
          background: var(--cal-header-bg);
          border-bottom: 1px solid var(--cal-header-border);
          color: var(--cal-header-text);
          position: relative;
        }

        .calendar-header-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 4px 8px;
          flex-wrap: wrap;
        }

        .calendar-header-left,
        .calendar-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .calendar-theme .calendar-title {
          color: var(--cal-header-text);
          font-weight: 600;
          font-size: 16px;
        }

        .cal-pill {
          background: rgba(255, 255, 255, 0.08);
          color: var(--cal-header-text);
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 6px 14px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
        }

        .cal-pill:hover {
          background: rgba(255, 255, 255, 0.16);
        }

        .cal-pill-dark {
          background: rgba(0, 0, 0, 0.2);
        }

        .cal-icon {
          width: 32px;
          height: 32px;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.08);
          color: var(--cal-header-text);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .cal-icon:hover {
          background: rgba(255, 255, 255, 0.16);
        }

        .cal-nav {
          display: inline-flex;
          gap: 6px;
        }

        .cal-view {
          position: relative;
        }

        .cal-dropdown {
          position: absolute;
          right: 0;
          top: calc(100% + 6px);
          background: #1e40af;
          border: 1px solid #1d4ed8;
          border-radius: 12px;
          padding: 6px;
          min-width: 120px;
          z-index: 5;
          box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
        }

        .cal-dropdown-item {
          width: 100%;
          text-align: left;
          border: 0;
          background: transparent;
          color: var(--cal-header-text);
          padding: 8px 10px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
        }

        .cal-dropdown-item:hover,
        .cal-dropdown-item.active {
          background: rgba(255, 255, 255, 0.14);
        }

        .calendar-search-pop {
          position: absolute;
          right: 8px;
          top: calc(100% + 6px);
          background: #ffffff;
          border-radius: 10px;
          padding: 8px;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
          z-index: 6;
        }

        .calendar-theme .calendar-body {
          background: var(--cal-bg);
        }

        .calendar-theme .calendar-weekdays {
          background: var(--cal-bg);
          border-bottom: 1px solid var(--cal-line);
          padding: 6px 10px;
        }

        .calendar-weekdays .col {
          color: var(--cal-muted);
          font-weight: 600;
          letter-spacing: 0.3px;
          font-size: 12px;
          text-transform: uppercase;
        }

        .calendar-grid {
          padding: 10px;
        }

        .calendar-grid.month-view .calendar-cell,
        .calendar-grid.week-view .calendar-cell {
          aspect-ratio: 1 / 1;
        }

        .calendar-grid.week-view .calendar-cell {
          aspect-ratio: auto;
          min-height: 600px;
        }

        .calendar-row {
          align-items: stretch;
        }

        .calendar-cell {
          display: flex;
          min-height: 0;
          overflow: hidden;
        }

        .calendar-cell-inner {
          height: 100%;
          width: 100%;
        }

        .calendar-grid > .calendar-row {
          background: var(--cal-surface);
          border: 1px solid var(--cal-line);
          border-top: none;
        }

        .calendar-grid > .calendar-row:first-child {
          border-top: 1px solid var(--cal-line);
          border-top-left-radius: 14px;
          border-top-right-radius: 14px;
          overflow: hidden;
        }

        .calendar-grid > .calendar-row:last-child {
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          overflow: hidden;
        }

        .calendar-cell {
          background: var(--cal-surface);
          border-right: 1px solid var(--cal-line);
        }

        .calendar-row .calendar-cell:last-child {
          border-right: none;
        }

        .calendar-cell.is-out {
          background: #fbfdff;
          color: #94a3b8;
        }

        .calendar-cell:hover {
          background: var(--cal-hover);
        }

        .calendar-date {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          font-weight: 600;
          font-size: 14px;
          color: var(--cal-text);
        }

        .calendar-date.is-out {
          color: #94a3b8;
        }

        .calendar-date.is-today {
          color: var(--cal-accent);
          border: 1px solid var(--cal-accent);
        }

        .calendar-date.is-selected {
          color: #fff;
          background: var(--cal-accent);
          border: 1px solid var(--cal-accent);
        }

        .event-line {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
        }

        .event-dot {
          width: 6px;
          height: 6px;
          border-radius: 999px;
          flex-shrink: 0;
        }

        .event-time {
          font-size: 11px;
          color: var(--cal-muted);
          flex-shrink: 0;
        }

        .event-title {
          flex: 1;
          min-width: 0;
          font-size: 12px;
          color: var(--cal-text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .time-badge, .time-badge-modal {
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
        }
        
        .day-view {
          min-height: 700px;
          background: var(--cal-surface);
          border-radius: 12px;
        }

        .day-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--cal-line);
        }

        .day-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .day-number {
          font-size: 28px;
          font-weight: 800;
          color: var(--cal-accent);
        }

        .day-name {
          font-size: 18px;
          font-weight: 700;
          color: var(--cal-text);
        }

        .day-pill {
          background: #e2e8f0;
          color: #0f172a;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
        }

        .day-count {
          color: var(--cal-muted);
          font-weight: 600;
          font-size: 13px;
        }

        .day-cards {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .day-card {
          display: flex;
          align-items: stretch;
          justify-content: space-between;
          gap: 16px;
          padding: 16px;
          border-radius: 14px;
          border: 1px solid var(--cal-line);
          background: #fff;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
        }

        .day-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 26px rgba(15, 23, 42, 0.12);
        }

        .day-card.is-meeting {
          border-left: 4px solid var(--cal-event-blue);
        }

        .day-card.is-activity {
          border-left: 4px solid var(--cal-event-purple);
        }

        .day-card-body {
          flex: 1;
          min-width: 0;
        }

        .day-card-top {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .day-time {
          font-size: 12px;
          font-weight: 700;
          color: var(--cal-text);
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 8px;
        }

        .day-chip {
          font-size: 11px;
          font-weight: 600;
          color: var(--cal-muted);
          border: 1px solid var(--cal-line);
          padding: 2px 8px;
          border-radius: 999px;
        }

        .day-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .day-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: var(--cal-event-blue);
          flex-shrink: 0;
        }

        .day-card.is-activity .day-dot {
          background: var(--cal-event-purple);
        }

        .day-card-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--cal-text);
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .day-meta {
          margin-top: 6px;
          font-size: 12px;
          color: var(--cal-muted);
        }

        .day-card-arrow {
          color: #94a3b8;
          font-size: 20px;
          font-weight: 700;
          display: flex;
          align-items: center;
        }
        
        .events-modal-list::-webkit-scrollbar {
          width: 6px;
        }
        
        .events-modal-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .events-modal-list::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        .events-modal-list::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        .flex-grow-1::-webkit-scrollbar {
          width: 3px;
        }
        
        .flex-grow-1::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .flex-grow-1::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.2);
          border-radius: 3px;
        }
        
        .flex-grow-1::-webkit-scrollbar-thumb:hover {
          background: rgba(0,0,0,0.3);
        }
        
        @media (max-width: 768px) {
          .day-view {
            padding: 1rem !important;
          }
          
          .modal-dialog {
            margin: 1rem;
          }
        }
        
        /* Performance optimizations */
        .event-item {
          will-change: transform, box-shadow;
        }
        
        .calendar-cell {
          will-change: background-color, box-shadow;
        }
        
        .event-card {
          will-change: transform, box-shadow;
        }
        
        .event-modal-item {
          will-change: transform, box-shadow, background-color;
        }
      `}</style>
    </>
  );
}
